<template>
  <div class="notification"></div>
</template>

<script>
import moment from "moment";
import { eventBus } from "@/main";
import { mapGetters, mapActions } from "vuex";
import debounce from "@/utils/debounce";

const QueueManageRoute = "QueueManage";
const QueueListRoute = "QueueList";
const DashboardRoute = "Dashboard";
const DoctorsTreatingQueueRoute = "DoctorsTreatingQueue";

import Pws from "pws";

// Constants for WebSocket configuration
const WEBSOCKET_CONFIG = {
  PING_INTERVAL: 4 * 60 * 1000, // 4 minutes
  PING_TIMEOUT: 5 * 60 * 1000, // 5 minutes
  DEBOUNCE_DELAY: 150, // 150ms
  INITIAL_RECONNECT_DELAY: 500, // 500ms
  MAX_RECONNECT_DELAY: 30000, // 30 seconds
  MAX_RECONNECT_ATTEMPTS: 5,
  MAX_ERROR_ATTEMPTS: 5,
  QUEUE_RECONNECT_DELAY: 5000, // 5 seconds
  TOKEN_REFRESH_COOLDOWN: 60000, // 1 minute
  TOKEN_REFRESH_RESET_DELAY: 5000, // 5 seconds
};

export default {
  name: "Notification",
  data() {
    return {
      isReconnect: false,
      isConnecting: false,
      isConnected: false,

      reconnectAttempts: 0,
      reconnectTimeout: null,
      delayTimeout: null,

      errorAttempts: 0,
      errorTimeout: null,

      pingInterval: null,
      lastPingTime: 0,
      lastTokenRefreshTime: 0,
      
      // Debounced message handlers - store references for cleanup
      debouncedDashboardHandler: null,
      debouncedQueueHandler: null,
      debouncedDoctorsTreatingHandler: null,

      // Cache route params to avoid repeated access
      cachedClinicUrl: null,
      cachedBranchUrl: null,
    };
  },

  mounted() {
    this.cacheRouteParams();
    this.connect();
    document.addEventListener("visibilitychange", this.queueReconnect);
  },
  watch: {
    getAccessToken() {
      this.reconnect();
    },
    '$route'() {
      // Update cached route params when route changes
      this.cacheRouteParams();
      // Reconnect to ensure correct WebSocket configuration
      this.reconnect();
    },
  },
  destroyed() {
    this.cleanup();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      authorization: "moduleUser/getAuthorization",
      isManager: "moduleUser/isManager",
      isDoctor: "moduleUser/isDoctor",
      isCounter: "moduleUser/isCounter",
      isHeadCounter: "moduleUser/isHeadCounter",
      isAssistant: "moduleUser/isAssistant",
      getAccessToken: "moduleAuth/getAccessToken",
    }),
    onQueueRoute() {
      return (
        this.$route.name === "QueueManage" || this.$route.name === "QueueList"
      );
    },
    onDashboardRoute() {
      return this.$route.name === "Dashboard";
    },
    onDoctorsTxQRoute() {
      return this.$route.name === "DoctorsTreatingQueue";
    },
  },
  methods: {
    ...mapActions({
      newCheckUserLogin: "moduleUser/newCheckUserLogin",
      updateStateQueueAppointment:
        "moduleAppointment/updateStateQueueAppointment",
      createStateQueueAppointment:
        "moduleAppointment/createStateQueueAppointment",
      deleteStateQueueAppointment:
        "moduleAppointment/deleteStateQueueAppointment",
      createStateDoctorAppointment:
        "moduleAppointment/createStateDoctorAppointment",
      updateStateDoctorAppointment:
        "moduleAppointment/updateStateDoctorAppointment",
      updateStateDoctorCheckInList:
        "moduleAppointment/updateStateDoctorCheckInList",
      deleteStateDoctorAppointment:
        "moduleAppointment/deleteStateDoctorAppointment",
      deleteStateDoctorCheckInList:
        "moduleAppointment/deleteStateDoctorCheckInList",
      fetchDoctorCheckInList: "moduleAppointment/fetchDoctorCheckInList",
      createStateWaitingForPaymentApptList:
        "moduleAppointment/createStateWaitingForPaymentApptList",
      deleteStateWaitingForPaymentApptList:
        "moduleAppointment/deleteStateWaitingForPaymentApptList",
      createStateDoctorCheckInList:
        "moduleAppointment/createStateDoctorCheckInList",

      fetchAllCheckInList: "moduleAppointment/fetchAllCheckInList",

      updateStateAllDoctorsAppointment:
        "moduleAppointment/updateStateAllDoctorsAppointment",
      createStateAllDoctorsAppointment:
        "moduleAppointment/createStateAllDoctorsAppointment",
      deleteStateAllDoctorsAppointment:
        "moduleAppointment/deleteStateAllDoctorsAppointment",
      updateStateAllDoctorsCheckInList:
        "moduleAppointment/updateStateAllDoctorsCheckInList",
      createStateAllDoctorsCheckInList:
        "moduleAppointment/createStateAllDoctorsCheckInList",
      deleteStateAllDoctorsCheckInList:
        "moduleAppointment/deleteStateAllDoctorsCheckInList",
      refreshToken: "moduleAuth/refreshToken",
    }),

    // Cache route parameters to avoid repeated access
    cacheRouteParams() {
      this.cachedClinicUrl = this.$route.params.clinicUrl;
      this.cachedBranchUrl = this.$route.params.branchUrl;
    },

    // Common message parsing and validation
    parseAndValidateMessage(message) {
      try {
        const data = JSON.parse(message.data);
        if (data.topic !== "/topic/appointment") return null;
        return data;
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
        return null;
      }
    },

    // Common appointment action handlers
    handleAppointmentAction(action, content, handlers) {
      const actionMap = {
        create: () => handlers.create?.(content),
        updateTo: () => handlers.create?.(content),
        update: () => {
          handlers.update?.(content);
          handlers.updateCheckIn?.(content);
        },
        move: () => {
          handlers.update?.(content);
          handlers.updateCheckIn?.(content);
        },
        confirmation: () => {
          handlers.update?.(content);
          handlers.updateCheckIn?.(content);
        },
        checkin: () => this.handleCheckinAction(content, handlers),
        cancelCheckin: () => this.handleCancelCheckinAction(content, handlers),
        refer: () => this.handleReferAction(content, handlers),
        delete: () => handlers.delete?.(content),
        updateFrom: () => handlers.delete?.(content.id),
        waitingForPayment: () => this.handleWaitingForPayment(content, handlers),
        cancelWaitingForPayment: () => this.handleCancelWaitingForPayment(content, handlers),
        payment: () => this.handlePayment(content, handlers),
        waitingForPaymentProcessing: () => this.handlePaymentProcessing(content, handlers),
        waitingForPrintingReceipt: () => handlers.update?.(content),
      };

      const handler = actionMap[action];
      if (handler) {
        handler();
      }
    },

    // Specific action handlers
    handleCheckinAction(content, handlers) {
      handlers.update?.(content);
      const startDt = moment(content.startDt).format("YYYY-MM-DD");
      if (startDt === moment().format("YYYY-MM-DD")) {
        const fetchParams = {
          date: startDt,
          clinicUrl: this.cachedClinicUrl,
          branchUrl: this.cachedBranchUrl,
        };
        
        if (handlers.fetchCheckIn) {
          const uid = content.doctorUid;
          if (uid) fetchParams.doctorUid = uid;
          
          handlers.fetchCheckIn(fetchParams).then(() => {
            handlers.onCheckin?.(content);
          });
        }
      }
    },

    handleCancelCheckinAction(content, handlers) {
      handlers.update?.(content);
      handlers.deleteWaitingForPayment?.(content.id);
      handlers.deleteCheckIn?.(content.id);
    },

    handleReferAction(content, handlers) {
      this.handleCheckinAction(content, handlers);
    },

    handleWaitingForPayment(content, handlers) {
      handlers.createWaitingForPayment?.(content);
      handlers.update?.(content);
      handlers.deleteCheckIn?.(content.id);
      handlers.onWaitingForPayment?.(content);
    },

    handleCancelWaitingForPayment(content, handlers) {
      handlers.deleteWaitingForPayment?.(content.id);
      handlers.update?.(content);
      handlers.createCheckIn?.(content);
      handlers.onCancelWaitingForPayment?.(content);
    },

    handlePayment(content, handlers) {
      handlers.deleteWaitingForPayment?.(content.id);
      handlers.update?.(content);
    },

    handlePaymentProcessing(content, handlers) {
      handlers.deleteWaitingForPayment?.(content.id);
      handlers.update?.(content);
    },


    dashboardMessageHandler: function (message) {
      const data = this.parseAndValidateMessage(message);
      if (!data) return;

      const { content, action } = data;
      const contentUid = content.doctorUid;

      // Only process messages for this doctor
      if (contentUid !== this.userInfo.uid) return;

      const handlers = {
        create: this.createStateDoctorAppointment,
        update: this.updateStateDoctorAppointment,
        updateCheckIn: this.updateStateDoctorCheckInList,
        delete: (id) => {
          this.deleteStateDoctorAppointment(id);
          this.deleteStateDoctorCheckInList(id);
        },
        deleteCheckIn: this.deleteStateDoctorCheckInList,
        createCheckIn: this.createStateDoctorCheckInList,
        fetchCheckIn: this.fetchDoctorCheckInList,
        onCheckin: (content) => {
          eventBus.$emit("blinkNewCheckIn", content);
          this.$parent.showDialogToast("warning", "คนไข้รอเข้าตรวจ");
          eventBus.$emit("playAudio", "waitingForTreatment");
        },
      };

      this.handleAppointmentAction(action, content, handlers);
    },
    queueMessageHandler: function (message) {
      const data = this.parseAndValidateMessage(message);
      if (!data) return;

      const { content, action } = data;

      const handlers = {
        create: this.createStateQueueAppointment,
        update: this.updateStateQueueAppointment,
        delete: this.deleteStateQueueAppointment,
        createWaitingForPayment: this.createStateWaitingForPaymentApptList,
        deleteWaitingForPayment: this.deleteStateWaitingForPaymentApptList,
        onWaitingForPayment: (content) => {
          eventBus.$emit("blinkNewPayment", content);
          this.$parent.showDialogToast("warning", "มีคนไข้รอชำระเงิน");
          eventBus.$emit("playAudio", "waitingForPayment");
        },
        onCancelWaitingForPayment: (content) => {
          eventBus.$emit("deleteWaitingForPayment", content);
        },
      };

      this.handleAppointmentAction(action, content, handlers);
    },
    doctorsTreatingQueueHandler: function (message) {
      const data = this.parseAndValidateMessage(message);
      if (!data) return;

      const { content, action } = data;

      const handlers = {
        create: this.createStateAllDoctorsAppointment,
        update: this.updateStateAllDoctorsAppointment,
        updateCheckIn: this.updateStateAllDoctorsCheckInList,
        delete: (id) => {
          this.deleteStateAllDoctorsAppointment(id);
          this.deleteStateAllDoctorsCheckInList(id);
        },
        deleteCheckIn: this.deleteStateAllDoctorsCheckInList,
        createCheckIn: this.createStateAllDoctorsCheckInList,
        fetchCheckIn: this.fetchAllCheckInList,
        onCheckin: (content) => {
          eventBus.$emit("notifyTxPt", content);
        },
      };

      this.handleAppointmentAction(action, content, handlers);
    },
    async reconnect() {
      if (this.isConnecting) {
        return;
      }
      
      this.clearReconnectTimeout();
      await this.disconnect();
      
      if (this.reconnectAttempts < WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS) {
        let delay = WEBSOCKET_CONFIG.INITIAL_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts);
        delay = Math.min(delay, WEBSOCKET_CONFIG.MAX_RECONNECT_DELAY); // Cap the delay
        
        this.delayTimeout = setTimeout(() => {
          this.connect();
        }, delay);
        this.reconnectAttempts++;
      }
    },
    async disconnect() {
      this.isConnecting = false;
      this.isConnected = false;
      
      if (this.websocketClient) {
        this.clearTimeoutAndInterval();
        this.clearReconnectTimeout();
        this.removeEventListeners();

        try {
          await this.websocketClient.close();
        } catch (error) {
          // Error closing WebSocket
        }
        
        this.websocketClient = null;
      }
    },

    // Complete cleanup for component destruction
    cleanup() {
      this.disconnect();
      this.clearDebounceTimers();
      document.removeEventListener("visibilitychange", this.queueReconnect);
      
      // Reset all state
      this.reconnectAttempts = 0;
      this.errorAttempts = 0;
      this.lastPingTime = 0;
      this.lastTokenRefreshTime = 0;
      this.cachedClinicUrl = null;
      this.cachedBranchUrl = null;
    },
    removeEventListeners() {
      if (!this.websocketClient) return;
      
      this.websocketClient.removeEventListener("open", this.handleWebsocketOpen);
      this.websocketClient.removeEventListener("message", this.handleWebsocketMessage);
      this.websocketClient.removeEventListener("error", this.handleWebsocketError);
      
      // Remove debounced handlers based on route
      switch (this.$route.name) {
        case DashboardRoute:
          if (this.debouncedDashboardHandler) {
            this.websocketClient.removeEventListener("message", this.debouncedDashboardHandler);
          }
          break;
        case QueueListRoute:
        case QueueManageRoute:
          if (this.debouncedQueueHandler) {
            this.websocketClient.removeEventListener("message", this.debouncedQueueHandler);
          }
          break;
        case DoctorsTreatingQueueRoute:
          if (this.debouncedDoctorsTreatingHandler) {
            this.websocketClient.removeEventListener("message", this.debouncedDoctorsTreatingHandler);
          }
          break;
      }
    },
    fetchData() {
      eventBus.$emit("fetch-queue-data");
    },
    queueReconnect() {
      if (this.isConnecting || (this.isConnected && this.websocketClient)) {
        return; // Don't queue reconnect if already connecting or connected
      }
      
      if (document.visibilityState === "visible" && !this.reconnectTimeout) {
        this.reconnectTimeout = setTimeout(() => {
          this.reconnect();
        }, WEBSOCKET_CONFIG.QUEUE_RECONNECT_DELAY);
      }
    },
    clearReconnectTimeout() {
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      clearTimeout(this.delayTimeout);
      this.delayTimeout = null;
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    },

    clearDebounceTimers() {
      // Reset debounced handler references for garbage collection
      this.debouncedDashboardHandler = null;
      this.debouncedQueueHandler = null;
      this.debouncedDoctorsTreatingHandler = null;
    },

    clearTimeoutAndInterval() {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    },

    ping() {
      if (this.pingInterval || !this.isConnected) return;
      
      this.pingInterval = setInterval(() => {
        if (!this.isConnected || !this.websocketClient) {
          this.clearTimeoutAndInterval();
          return;
        }
        
        try {
          const message = JSON.stringify({
            data: {
              type: "ping",
              message: "ping pong",
            },
          });
          this.websocketClient.send(message);
          this.lastPingTime = Date.now();
        } catch (error) {
          console.error('Error sending ping:', error);
          this.queueReconnect();
        }
      }, WEBSOCKET_CONFIG.PING_INTERVAL);
    },

    connect() {
      if (this.isConnecting || this.isConnected) {
        return;
      }
      
      this.isConnecting = true;
      
      try {
        let clinicURL = this.$route.params.clinicUrl;
        let branchURL = this.$route.params.branchUrl;
        const topic = "appointment";
        const urlParams = new URLSearchParams({
          clinicURL,
          branchURL,
          topic,
          authorization: `Bearer ${localStorage.getItem("access_token")}`,
        });
        switch (this.$route.name) {
          case DashboardRoute:
            urlParams.append("doctorUID", this.authorization.uid);
            break;
        }
        const wsURL = new URL(process.env.VUE_APP_WS_URL);
        wsURL.search = urlParams;
        this.websocketClient = new Pws(
          wsURL.toString(),
          {
            pingTimeout: WEBSOCKET_CONFIG.PING_TIMEOUT,
          }
        );

        this.websocketClient.addEventListener("open", this.handleWebsocketOpen);
        this.websocketClient.addEventListener(
          "message",
          this.handleWebsocketMessage
        );
        
        // Create debounced message handlers if not already created
        if (!this.debouncedDashboardHandler) {
          this.debouncedDashboardHandler = debounce(this.dashboardMessageHandler.bind(this), WEBSOCKET_CONFIG.DEBOUNCE_DELAY);
        }
        if (!this.debouncedQueueHandler) {
          this.debouncedQueueHandler = debounce(this.queueMessageHandler.bind(this), WEBSOCKET_CONFIG.DEBOUNCE_DELAY);
        }
        if (!this.debouncedDoctorsTreatingHandler) {
          this.debouncedDoctorsTreatingHandler = debounce(this.doctorsTreatingQueueHandler.bind(this), WEBSOCKET_CONFIG.DEBOUNCE_DELAY);
        }
        
        switch (this.$route.name) {
          case DashboardRoute:
            this.websocketClient.addEventListener("message", this.debouncedDashboardHandler);
            break;
          case QueueListRoute:
          case QueueManageRoute:
            this.websocketClient.addEventListener("message", this.debouncedQueueHandler);
            break;
          case DoctorsTreatingQueueRoute:
            this.websocketClient.addEventListener("message", this.debouncedDoctorsTreatingHandler);
            break;
          default:
          // do nothing
        }

        this.websocketClient.addEventListener(
          "error",
          this.handleWebsocketError
        );

        this.ping();
      } catch (error) {
        console.error("WebSocket error with :", error);
        this.isConnecting = false;
        this.queueReconnect();
      }
    },
    handleWebsocketOpen() {
      this.isConnecting = false;
      this.isConnected = true;
      
      if (this.isReconnect) {
        this.fetchData();
      }
      this.clearReconnectTimeout();
      this.isReconnect = true;
      this.reconnectAttempts = 0;
      this.errorAttempts = 0;
    },
    handleWebsocketMessage(e) {
      this.clearReconnectTimeout();
      const data = JSON.parse(e.data);
      if (data.statusCode === 401) this.refreshToken();
      if (data.message === "Internal server error") this.queueReconnect();
    },
    handleWebsocketError(e) {
      if (this.errorAttempts < WEBSOCKET_CONFIG.MAX_ERROR_ATTEMPTS) {
        const delay = WEBSOCKET_CONFIG.INITIAL_RECONNECT_DELAY * Math.pow(2, this.errorAttempts);
        this.errorTimeout = setTimeout(() => {
          this.refreshTokenWithCooldown();
        }, delay);
        this.errorAttempts++;
      }
    },
    refreshTokenWithCooldown() {
      const currentTime = Date.now();
      
      if (currentTime - this.lastTokenRefreshTime >= WEBSOCKET_CONFIG.TOKEN_REFRESH_COOLDOWN) {
        this.lastTokenRefreshTime = currentTime;
        this.refreshToken();
      }
    },
  },
};
</script>

<style></style>
